import {
  Environment,
  LanguageSelector,
  Loader,
  localForageHelper,
  loginBgPngDesktop,
  loginBgPngMobile,
  Logo,
  LogoFooter,
  MySquid,
  onConfigChange,
  onEnvironmentChange,
  onUserChange,
  simbolSvg,
  ToastHelper,
  ToggleTheme,
  User,
  UserHelper,
} from '@hub/shared'
import BindPage from '@src/pages/bind/bind.page'
import ForgotPasswordPage from '@src/pages/forgot-password/forgot-password.page'
import LoginPage from '@src/pages/login/login.page'
import NewPassword from '@src/pages/new-password/new-password.page'
import RegisterPageComponent from '@src/pages/register/register.page'
import VerifyPage from '@src/pages/verify/verify.page'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Route, Routes, useLocation } from 'react-router-dom'
import { SubscriptionLike } from 'rxjs'

import './login.layout.scoped.scss'
import './login.layout.scss'

const NoMatch = () => {
  history.pushState({}, '', '/error/not-found')
  return <div />
}

export default (props: any) => {
  const toast = new ToastHelper().toast
  const onEnvironmentChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const [urlCreators, setUrlCreators] = useState<string>('')
  const onConfigChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const onUserChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const userHelper = useMemo(() => new UserHelper(), [])
  const [newUser, setNewUser] = useState<User | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [loadingLoader, setLoadingLoader] = useState<boolean>(false)
  const hadHash = useMemo(() => !!window?.location?.hash, [])
  const [config, setConfig] = useState<MySquid | null>(null)
  const { i18n, t } = useTranslation()
  const { search, pathname } = useLocation()
  const links = [
    {
      label: 'wake',
      href: config?.aboutLink ? config?.aboutLink : 'https://squidit.com.br/',
    },
    {
      label: 'help',
      href: config?.whatsappLink ? config?.whatsappLink : urlCreators,
    },
    {
      label: 'terms',
      href: '/contract/plataform',
    },
    {
      label: 'privacy',
      href: '/contract/policyprivate',
    },
  ]

  useEffect(() => {
    userHelper.getUser()
  }, [userHelper])

  useEffect(() => {
    onUserChangeSubscription.current = onUserChange.subscribe((userChanged: User) => {
      if (userChanged?.squidId && !newUser) {
        setNewUser(userChanged)
      }
      setLoading(false)
    })
    onConfigChangeSubscription.current = onConfigChange.subscribe((configChanged) => {
      if (typeof configChanged !== 'boolean') {
        if (configChanged?._id) {
          setConfig(configChanged)
        }
      }
    })
    return () => {
      onConfigChangeSubscription.current?.unsubscribe()
      onUserChangeSubscription.current?.unsubscribe()
    }
  }, [onUserChangeSubscription, onConfigChangeSubscription, newUser])

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null
    if (search) {
      const query = new URLSearchParams(search)
      if (query.get('logout') === 'true') {
        userHelper.deleteUser()
        query.delete('logout')
      }
      if (query.get('follow_link')?.length) {
        localForageHelper.setItem('follow_link', query.get('follow_link'))
        query.delete('follow_link')
      }
      if (query.get('token_expired') === 'true') {
        timer = setTimeout(() => {
          toast.warning(t('loginLayout:messages.token_expired'), {
            closeButton: true,
            className: 'full text-bold',
            position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
            notOverClick: true,
            immediately: true,
          })
          query.delete('token_expired')
        }, 200)
      }
      const queryParams = query?.toString()?.length ? `?${query.toString()}` : ''
      history.replaceState({}, '', `${pathname}${queryParams}`)
    }

    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [userHelper, search, pathname, t, toast])

  useEffect(() => {
    onEnvironmentChangeSubscription.current = onEnvironmentChange.subscribe((environments: Environment) => {
      setUrlCreators(environments?.squidProducts?.creators)
    })
    return () => onEnvironmentChangeSubscription?.current?.unsubscribe()
  }, [])

  useEffect(() => {
    const onConfigChangeObservable: SubscriptionLike = onConfigChange.subscribe((newConfig: MySquid | any) => {
      setConfig(newConfig)
    })

    return () => {
      onConfigChangeObservable?.unsubscribe()
    }
  }, [])

  const verifyValidatedMail = useCallback(() => {
    const query = new URLSearchParams(location.search)
    const params = query.get('mail_verified')
    const loginMessage = query.get('login_message')
    if (params === 'true') {
      toast.success(t('loginLayout:messages.mail_verified'), {
        closeButton: true,
        className: 'full text-bold',
        position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
        notOverClick: true,
        immediately: true,
      })
      query.delete('mail_verified')
    }
    if (loginMessage === 'password_redefined') {
      toast.success(t('loginLayout:messages.password_redefined'), {
        closeButton: true,
        className: 'full text-bold',
        position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
        notOverClick: true,
        immediately: true,
      })
      query.delete('login_message')
    }
    if (loginMessage === 'removed_success') {
      toast.success(t('loginLayout:messages.removed_success'), {
        closeButton: true,
        className: 'full text-bold',
        position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
        notOverClick: true,
        immediately: true,
      })
      query.delete('login_message')
    }
    const queryParams = query?.toString()?.length ? `?${query.toString()}` : ''
    history.replaceState({}, '', `${pathname}${queryParams}`)
  }, [t, toast, pathname])

  useEffect(() => {
    const timer = setTimeout(() => verifyValidatedMail(), 200)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  useEffect(() => {
    if (
      newUser &&
      !loadingLoader &&
      !window.location.pathname.includes('new-password') &&
      window.location.pathname.includes('login') &&
      !hadHash
    ) {
      history.pushState({}, '', '/home')
    }
  }, [newUser, loadingLoader, hadHash])

  return (
    <div>
      {loading ? (
        <div className="main-loader">
          <Loader size="bigger" />
        </div>
      ) : null}
      <div className={`login-layout ${loading ? 'display-none' : ''}`}>
        <style>
          {`.bg {
            background-image: ` +
            'linear-gradient(269.78deg, rgba(0, 0, 0, 0.2) -2.49%, rgba(0, 0, 0, 0) 34.22%), ' +
            `url(${config?._id ? config?.images?.backgroundDesktop?.url : loginBgPngDesktop}); ` +
            `}
          @media (max-width: 991px) {
            .bg {
              background-image: ` +
            `url(${config?._id ? config?.images?.backgroundMobile?.url : loginBgPngMobile}); ` +
            `}
          }
        `}
        </style>
        <figure className="image bg">
          {config?._id && config?.graphics ? (
            <span
              className={`${
                !config?.images?.backgroundMobile?.url || config?.images?.backgroundMobile?.url === 'undefined' ? 'mobile' : ''
              } ${!config?.images?.backgroundDesktop?.url || config?.images?.backgroundDesktop?.url === 'undefined' ? 'desktop' : ''}`}
              style={{ backgroundImage: `url(${simbolSvg})` }}
              title="Wake Creators"
            />
          ) : null}
        </figure>
        <aside>
          <header>
            <Logo className="text-right logo-desk" style={{ maxWidth: 'initial' }} />
          </header>
          <main>
            <span className={'logo-inside'}>
              <Logo style={{ maxWidth: 'initial' }} />
            </span>
            <Routes>
              <Route path="/" element={<LoginPage {...props} setLoadingLoader={setLoadingLoader} />} />
              <Route path="/verify" element={<VerifyPage {...props} />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage {...props} />} />
              <Route path="/register" element={<RegisterPageComponent {...props} />} />
              <Route path="/bind" element={<BindPage {...props} />} />
              <Route path="/new-password" element={<NewPassword {...props} />} />
              <Route path="*" element={<NoMatch />} />
            </Routes>
          </main>
          <footer>
            {links?.length > 0 ? (
              <ul className="links">
                {links?.map((link) => {
                  return (
                    <li key={link?.label}>
                      <a href={link.href} key={link.label} target="_blank">
                        {t(`loginLayout:links.${link.label}`, { organization: config ? config?.name : 'Wake Creators' })}
                      </a>
                    </li>
                  )
                })}
                <li className="toggle-theme">
                  <ToggleTheme fontSize="0.9rem" />
                </li>
              </ul>
            ) : null}
            <div className="lang-logo">
              {config?._id ? (
                <a href="https://wakecreators.com.br/" target="_blank">
                  <LogoFooter width="6rem" height="6rem" />
                </a>
              ) : null}
              <div className="lang-selector">
                <LanguageSelector lang={i18n.language as 'en' | 'pt' | 'es'} />
              </div>
            </div>
          </footer>
        </aside>
      </div>
    </div>
  )
}
